// /src/renderer/analysis/rendering/Panels/renderers/Surface3DPanel.tsx
import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';

import '../PanelContainer.scss';
import { PanelCommonProps, PanelComponentKeyType } from 'config/panelConfig';
import PanelOverlay from './PanelOverlay';
import { usePanelContainer } from '../usePanelContainer';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';

import vtkCamera from '@kitware/vtk.js/Rendering/Core/Camera';
import useResizeObserver from '@features/view/useResizeObserver';

import { PANEL_ADJUSTMENT_NAMES } from 'config/labels';
import SimpleContextMenu, {
  SimpleContextMenuItem,
} from '@components/ContextMenu/SimpleContextMenu';

import {
  deactivateExclusiveTools,
  getRequestSegmentation,
  getVolumeFromSeriesId,
  loadLAWT,
  updateLabelmapSegment,
  updateManualSegStyle,
} from './CornerstoneManager';

import { useBrowserPage } from '@features/browser/useBrowserPage';
import useAuth from '@hooks/Auth/useAuth';
import {
  EditNames,
  getSegmentationAnalysisType,
  getSegShapeAnalysisType,
  isVRSurfaceSwitchType,
  Segmentation,
  SegmentationControllerType,
} from 'config/analysisConfig';
import {
  axesLineRenderer,
  cleanUpVtkRenderer,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  initializeVtkRenderer,
  SurfaceContext,
  useBufferPool,
  requestTerrData,
  setContainer,
  setActorsCenter,
  updateResetCamera,
  territoryInfo,
  handleMouseWheel,
  enterOrExitManualSegmentationVtk,
  updateCameraForAnalysis,
  updateRegulatorConnect3DMarkers,
  updateRenderWindow,
  disposeServerFrontDataVtkContext,
} from './vtkManagers';

import {
  AorticNodeRenderer,
  heartCutter,
  initCameraForAnalysis,
  mappingRenderer,
  createTerritoryCenterLine,
  segmentation3DReader,
  segmentationFFRReader,
  findClosestScalarId,
  getFFRCenterPoint,
  getFFRCenterId,
  getIndexToWorld,
  updateAmbientLight,
} from '@features/view/Surface3DUtilities';

import {
  centerLineRender,
  handleVisibility,
  CPRNodeAllRenderer,
  taviNodeAllRenderer,
  setActorVisible,
  setActorOpacity,
} from '@features/view/Shared3DUtilities';

import { renderers, scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import { getConfiguredCprCrossSectionImageMode } from '../cprImageModeConfig';
import { getServerFrontDataAvailability } from '../cprImageModeState';
import { isServerCprFrontDataAnalysisType } from '../frontDataCprDebugLoader';
import SvgTextRenderer from '@features/view/SvgTextRender';
import SvgMeasurementText from '@features/view/SvgMeasurementText';
import { useBranchInfo } from '@contexts/BranchContext';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import SvgColorScaleBar from '@features/view/SvgColorScaleBar';
import { useConfig } from 'config/useConfig';
import type { Surface3DOverlayLabel } from 'config/config';
import { SavedInfo } from '@models/Parameter';
import D3Table from '@components/SVGTable/SVGTable';
import useModalActions from '@hooks/common/useModalAction';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import { applyMyoCardialConfig } from '@features/view/utilitiesManager';
import { useLabelNamingText } from '@features/view/useLabelNamingText';
import { use3DRendererManager } from '@hooks/useAnalysisForPanel/use3DRendererManager';
import { usePanelOverlayText } from '@hooks/useAnalysisForPanel/usePanelOverlayText';
import { updateOtherViews } from '@hooks/useAnalysisForPanel/useDicomUtils';
import useKeyboardShortcut from '@hooks/useKeyboardShortcut';
import { restoreRendererActors, useSegmentManager } from '@features/analysis/segmentManager';
import { FFR_OPACITY, FFRNames, nodeNames } from 'config/render3DConfig';
import { getDisplayRuntimeProfile } from 'config/displayRuntime';
import SvgDrawLine, { type SvgDrawLineHandle } from '@features/view/SvgDrawLine';
import {
  buildInitialPointSphereMarkerSpecs,
  clearInitialPointSphereMarkers,
  setInitialPointSphereVisibility,
  syncInitialPointSphereMarkers,
} from '@features/view/initialPointSphereMarkers';

// import test from 'img/test.jpg';
// Series Title
// (Patient Name) - (Patient Age) - (Acquisition Date) - (Acquisition Time) - (Series Description)
// text 표기, 정보 없으면 delimiter 제외, 공백

// Series Description
// -(Acquisition Date) (Acquisition Time)
// -(Accession Number)
// -(Study Description)
// -(Series Description)
// -(Image Comments)
// -Series: (Series Number)
// text 표기, 정보 없으면 공백

// Patient Description
// -(Institution Name)
// -(Patient Name)
// -(Patient ID)
// -(Patient Birth Date) (Patient Sex) (Patient Age)

// Image Controller Description
// -X: (pointX) Y: (pointY) Z: (pointZ) Value: (mouse Pixel Value) (3D Focus 시)
// 랜더 타입(변경가능, Axial,coronal,sagittal,Report)

// 3D Surface Orientation Reformatting Description
// Axial, Coronal, Sagittal방향에 맞는 컬러로 이루어진 회전박스(면 클릭으로 화면전환)
// 오일러 각 X,Y,Z로부터 Yaw,Pitch,Roll값을 표시
// WL없음
type Surface3DPanelPopup = {
  [Key in keyof typeof PANEL_ADJUSTMENT_NAMES]?: boolean;
};
enum StateLAWT {
  NONE = 0,
  READY = 1,
  COMPLETE = 2,
}
const initialPopup: Surface3DPanelPopup = {
  projection: false,
  preset: false,
};

const Surface3DPanel: React.FC<PanelCommonProps> = ({
  className,
  panelConfig,
  possibleProjection,
  topLeftContent,
  topRightContent,
}: PanelCommonProps) => {
  // 패널 내 오버레이에 필요한 상태나 계산 로직이 있을 수 있음
  const ref = useRef<HTMLDivElement>(null);
  const context = useRef<SurfaceContext>(null);

  const { accessToken } = useAuth();

  const { selectedSeries, selectedSeriesId } = useBrowserPage();

  const {
    isPreview,
    isLayoutStateLoading,
    globalToolState,
    panelToolState,
    selectedLayoutConfig,
  } = useAnalysisPage();

  const {
    analysisType,
    selectedPanelComponentKey,
    fullscreenPanelComponentKey,
    updateSelectedPanelConfig,
    panelStates,
    updatePanelState,
    swapLayoutPanelConfig,
    updateFullscreenPanelComponentKey,
  } = usePanelContainer();

  const [yawPicthRoll, setYawPicthRoll] = useState<{
    yaw: number;
    pitch: number;
    roll: number;
  }>({ yaw: 0, pitch: 0, roll: 0 });
  const [stateLAWT, setStateLAWT] = useState<StateLAWT>(StateLAWT.NONE);
  const [readyState, setReadyState] = useState({ panel: false, camera: false });

  // Panel의 상태
  const panelState = panelStates[panelConfig.componentKey];
  const { segmentation } = panelState;
  const { mainBranchIds } = useBranchInfo();
  const { bufferPool, contextPool, setContextPool, setBufferPool } =
    useBufferPool();

  //CPR 관련
  const { config, getColors, setData } = useConfig();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const coronaryState = panelStates['CPR'].coronary!;
  const stateMap = coronaryState.state;
  const [svgShowText, setSvgShowText] = useState<boolean>(false);
  const prevDataRef = useRef<Record<string, any>>({});
  const jsonRef = useRef<ArrayBuffer | undefined>(undefined);
  const lowQuality = config.generalSetting.renderingQuality !== 'high';
  const isServerFrontDataVtkMode =
    getConfiguredCprCrossSectionImageMode() === 'server' &&
    isServerCprFrontDataAnalysisType(analysisType) &&
    getServerFrontDataAvailability(pool) !== 'missing';
  const isLesionStackViewLayout =
    analysisType === 'plaque' && selectedLayoutConfig.type === 'lesionStackView';
  const surfaceServerGenerationRef = useRef(0);

  const isStaleServerSurfaceGeneration = useCallback(
    (generation: number) =>
      isServerFrontDataVtkMode &&
      (surfaceServerGenerationRef.current !== generation ||
        context.current?.activeAnalysisType !== analysisType ||
        context.current?.activeSeriesId !== selectedSeriesId),
    [isServerFrontDataVtkMode, analysisType, selectedSeriesId],
  );

  const shouldRecreateServerVtkContext = useCallback(
    (candidate?: SurfaceContext | null) => {
      if (!candidate) return false;
      if (!candidate.serverFrontDataVtkMode && !isServerFrontDataVtkMode) {
        return false;
      }
      return (
        candidate.serverFrontDataVtkDisposed === true ||
        candidate.serverFrontDataVtkMode !== isServerFrontDataVtkMode ||
        candidate.activeAnalysisType !== (isServerFrontDataVtkMode ? analysisType : undefined) ||
        candidate.activeSeriesId !== (isServerFrontDataVtkMode ? selectedSeriesId : undefined)
      );
    },
    [isServerFrontDataVtkMode, analysisType, selectedSeriesId],
  );

  const requestSegmentation = useMemo(() => {
    const requested = getRequestSegmentation(analysisType, segmentation!);
    if (analysisType !== 'FFR') return requested;

    const { coronary, ...rest } = requested as typeof requested & {
      coronary?: Segmentation['coronary'];
    };
    return rest;
  }, [analysisType]);

  const { waitingAction, closeLoadingModal } = useModalActions();
  const { mode: manualMode } = useSegmentManager();
  const svgDrawLineRef = useRef<SvgDrawLineHandle>(null);

  const clearSvgDrawLine = useCallback(() => {
  }, []);

  type Surface3DOverlayKey = keyof Surface3DOverlayLabel;
  const surface3DOverlayLabel = useMemo(
    () =>
      config.viewOverlayLabels?.surface3D ?? {
        lesion: true,
        centerline: true,
        label: true,
        stenosisLesion: true,
      },
    [config.viewOverlayLabels?.surface3D],
  );
  const [surface3DOverlayMenuOpen, setSurface3DOverlayMenuOpen] =
    useState(false);
  const shouldShowSurface3DOverlayMenu = useMemo(
    () => ['plaque', 'fai', 'stenosis', 'calciumScore', 'isr'].includes(analysisType),
    [analysisType],
  );

  const applySurface3DOverlayVisibility = useCallback(
    (visibility: Surface3DOverlayLabel = surface3DOverlayLabel) => {
      if (!context.current) return;
      const actorIds = context.current.actorIds ?? {};
      const setVisibleByKey = (
        predicate: (key: string) => boolean,
        visible: boolean,
      ) => {
        Object.entries(actorIds).forEach(([key, actor]) => {
          if (!predicate(key)) return;
          (actor as vtkActor).setVisibility(visible);
        });
      };
      setVisibleByKey(
        (key) => new RegExp(`^${nodeNames.node}\\.\\d+\\.`).test(key),
        visibility.lesion !== false,
      );
      setVisibleByKey(
        (key) =>
          key === nodeNames.singleCenterLine ||
          key.startsWith(`${nodeNames.centerLine}.`),
        visibility.centerline !== false,
      );
      setVisibleByKey(
        (key) => key.startsWith(`${nodeNames.stenosis}.`),
        visibility.stenosisLesion !== false,
      );
      if (analysisType === 'plaque' && isLesionStackViewLayout) {
        setVisibleByKey(
          (key) =>
            new RegExp(`^${nodeNames.node}\\.\\d+\\.1$`).test(key),
          false,
        );
      }
      updateRenderWindow(context);
    },
    [analysisType, context, isLesionStackViewLayout, surface3DOverlayLabel],
  );

  useEffect(() => {
    applySurface3DOverlayVisibility();
  }, [applySurface3DOverlayVisibility]);

  const surface3DOverlayMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    const overlayItems: Array<{
      key: Surface3DOverlayKey | 'all';
      label: string;
      show: boolean;
    }> = [
      { key: 'all', label: 'All', show: true },
      { key: 'lesion', label: 'Lesion', show: analysisType !== 'calciumScore' },
      { key: 'centerline', label: 'Centerline', show: true },
      { key: 'label', label: 'Label', show: true },
      {
        key: 'stenosisLesion',
        label: 'StenosisLesion',
        show: analysisType === 'stenosis',
      },
    ];
    const visibleItems = overlayItems.filter((item) => item.show);
    const visibleKeys = visibleItems
      .map((item) => item.key)
      .filter((key): key is Surface3DOverlayKey => key !== 'all');
    const allTrue =
      visibleKeys.length > 0 &&
      visibleKeys.every((key) => surface3DOverlayLabel[key] !== false);

    const applyNextOverlayLabel = (nextLabel: Surface3DOverlayLabel) => {
      setData((prev: any) => ({
        viewOverlayLabels: {
          ...prev.viewOverlayLabels,
          surface3D: nextLabel,
        },
      }));
      applySurface3DOverlayVisibility(nextLabel);
    };

    const toggle = (key: Surface3DOverlayKey | 'all') => {
      if (key === 'all') {
        const nextValue = !allTrue;
        const nextLabel = { ...surface3DOverlayLabel };
        visibleKeys.forEach((visibleKey) => {
          nextLabel[visibleKey] = nextValue;
        });
        applyNextOverlayLabel(nextLabel);
        return;
      }

      const nextLabel = {
        ...surface3DOverlayLabel,
        [key]: !surface3DOverlayLabel[key],
      };
      applyNextOverlayLabel(nextLabel);
    };

    return visibleItems.map((item) => ({
      label: item.label,
      onClick: () => toggle(item.key),
      isChecked:
        item.key === 'all'
          ? allTrue
          : surface3DOverlayLabel[item.key] !== false,
    }));
  }, [
    analysisType,
    applySurface3DOverlayVisibility,
    setData,
    surface3DOverlayLabel,
  ]);

  const surface3DOverlayButtonContent = useMemo(() => {
    if (!shouldShowSurface3DOverlayMenu) return null;
    return (
      <div
        className={`projection-text ${panelConfig.componentKey}`}
        onClick={(event) => {
          event.stopPropagation();
          setSurface3DOverlayMenuOpen(true);
        }}
      >
        Overlays
      </div>
    );
  }, [panelConfig.componentKey, shouldShowSurface3DOverlayMenu]);

  const applyLesionStackMidNodeVisibility = useCallback(() => {
    if (analysisType !== 'plaque') return;
    const actorIds = context.current?.actorIds;
    if (!actorIds) return;

    let changed = false;
    const visible = !isLesionStackViewLayout;
    Object.entries(actorIds).forEach(([key, actor]) => {
      if (!key.startsWith(`${nodeNames.node}.`) || !key.endsWith('.1')) return;
      const vtkActor = actor as vtkActor;
      if (vtkActor.getVisibility?.() === visible) return;
      vtkActor.setVisibility(visible);
      changed = true;
    });

    if (changed) {
      updateRenderWindow(context);
    }
  }, [analysisType, isLesionStackViewLayout]);

  useEffect(() => {
    applyLesionStackMidNodeVisibility();
  }, [applyLesionStackMidNodeVisibility]);

  //plaue에 사용되는 인자 //  render view 단 하나만 사용해서 강제로 0 입력
  const plaueBranchMainId = coronaryState.current[0];
  const {
    CArmAngles,
    wallThicknessValue,
    resetNodeRef,
    handleFocus,
    handleMarkerMove,
    handleRotate,
    handleAxes,
    handleUpdateRenderWindow,
    handleReset,
    handleOpenCArmAnglesModal,
    measurementSvgRef,
    measurementLabelItems,
    updateMeasurementLabelPositions,
    updateFFRCentline,
  } = use3DRendererManager({
    context,
    lowQuality,
  });

  const {
    svgRef,
    updateTextBoxPositions,
    handleFFRPickPoints,
    handlehideFFRPoints,
    canRenderSvgText,
    filteredCenterlineItems,
    handleDelete,
    handleResetDefaultPoints,
    handleClearFFRPoints,
  } = useLabelNamingText({
    context,
    pool,
    lowQuality,
  });

  // useEffect(() => {
  //   // 캡쳐할때 브런치 이름 잠시 안보이게 하는거
  //   if (analysisType === 'FFR') {
  //     if (config.isCapture) {
  //       setSvgShowText(true);
  //       return;
  //     }
  //   }
  // }, [config.isCapture]);

  renderers[panelConfig.componentKey] = {
    shouldSuppressRenderError: () => isServerFrontDataVtkMode,
    render: (args?: any) => {
      const { type } = args;
      if (type === 'focus') {
        const { mode, world, pickId, buttonType } = args;
        if (analysisType === 'FFR' && mode === 'mouse') {
          const isRightClick = buttonType === 'Right';
          const FFR_TARGETS = [
            FFRNames.main_lca,
            FFRNames.main_rca,
            FFRNames.cap_lca,
            FFRNames.cap_rca,
          ];

          const isFFRTarget = FFR_TARGETS.includes(pickId);

          if (isRightClick && !isFFRTarget) {
            setData({ ffr_selected_centerline_id: null });
            //백그라운드 오른쪽 클릭할 때
            setActorVisible(context, false, [nodeNames.singleCenterLine]);

            setActorOpacity(context, FFR_TARGETS, FFR_OPACITY.FULL);
            updateRenderWindow(context);
            return;
          }

          if (isFFRTarget) {
            const point = [-world[0], world[1], world[2]] as [
              number,
              number,
              number,
            ];

            const info = findClosestScalarId(context, pickId, point);
            if (!info) {
              console.log('없다');
              return;
            }

            const branchId = getFFRCenterId(
              context,
              analysisType,
              pickId,
              info.pointId,
            );
            if (buttonType == 'Right') {
              if (branchId !== undefined) {
                setData({ ffr_selected_centerline_id: branchId });
                console.log('[FFR pick] centerline id selected', {
                  pickId,
                  pointId: info.pointId,
                  centerlineId: branchId,
                });

                updateFFRCentline(branchId);
              }
            } else {
              if (branchId !== undefined) {
                handleFFRPickPoints(pickId, info.pointId, point, branchId);
              }
            }

            return;
          }
        }
        handleFocus(args);
        // } else if (type === 'SegmentedRegionOnoff') {
        //   const segmentation = panelStates.Surface3D.segmentation;
        //   if (!segmentation) return;
        //   const targetKeys: SegmentationControllerType[] =
        //     getSegmentationAnalysisType(analysisType);
        //   const newSegmentation = { ...segmentation };
        //   for (const key of targetKeys) {
        //     const current = segmentation[key];
        //     newSegmentation[key] = {
        //       ...current,
        //       opacity: current.opacity === 0 ? 1 : current.opacity,
        //       visibility: !current.visibility,
        //     };
        //     updatePanelState(key, 'segmentation', newSegmentation);
        //     updateLabelmapSegment({
        //       segmentTarget: key,
        //       segmentations: newSegmentation,
        //     });
        //   }
      } else if (type === 'eraserPointFFR') {
          handleClearFFRPoints();
      } else if (type === 'rotate') {
        handleRotate(args);
      } else if (type === 'switchAxes') {
        handleAxes(args);
      } else if (type === 'updateRenderWindow') {
        handleUpdateRenderWindow(args);
      } else if (type === 'regulatorConnectMarkers') {
        updateRegulatorConnect3DMarkers(context, args);
      }
      if (['markerMove', 'polygonMove'].includes(type)) {
        if (analysisType === 'FFR') {
          const { idx, branchId, ffrData } = args;
          const point = getFFRCenterPoint(
            context,
            pool!,
            selectedSeriesId!,
            analysisType,
            branchId,
            idx,
          );
          if (!point) return;
          handleFFRPickPoints(
            nodeNames.singleCenterLine,
            idx,
            point,
            branchId,
            ffrData,
          );
        } else {
          handleMarkerMove(args);
          if (isLesionStackViewLayout) {
            applyLesionStackMidNodeVisibility();
          }
        }
      } else if (type === 'stenosisUpdate') {
        if (isServerFrontDataVtkMode && analysisType !== 'stenosis') return;
        centerLineRender(context, pool!, analysisType, []);
      } else if (type === 'reset') {
        handleReset(args, globalToolState?.toggles.ToggleAxes!);
        if (analysisType === 'FFR') {
          handleResetDefaultPoints();

          const FFR_TARGETS = [
            FFRNames.main_lca,
            FFRNames.main_rca,
            FFRNames.cap_lca,
            FFRNames.cap_rca,
          ];
          setData({ ffr_selected_centerline_id: null });
          //백그라운드 오른쪽 클릭할 때
          setActorVisible(context, false, [nodeNames.singleCenterLine]);

          setActorOpacity(context, FFR_TARGETS, FFR_OPACITY.FULL);
          updateRenderWindow(context);
          return;
        }
      } else if (type === 'runHeartCutter') {
        if (!context.current) return;
        heartCutter(context, bufferPool, config.heartCut, selectedSeriesId!);
        return;
      } else if (type === 'aorta_node') {
        if (analysisType !== 'aorticValveCalcium') return;
        const { world, normal } = args;
        AorticNodeRenderer(context, world, normal);
      } else if (type === 'updateLabelmap') {
        if (!selectedSeriesId || !panelStates.Surface3D.segmentation) return;
        const seg = getRequestSegmentation(
          analysisType,
          panelStates.Surface3D.segmentation,
        );
        (Object.keys(seg) as (keyof typeof seg)[]).forEach((key) => {
          handleVisibility(
            context,
            analysisType,
            key,
            seg[key],
            setSvgShowText,
          );
        });
        applyLesionStackMidNodeVisibility();
        applySurface3DOverlayVisibility();
      } else if (type === 'updateSegmentation') {
        const { name, newSegmentation, visibleFFR } = args;
        if (analysisType === 'FFR') {
          if (visibleFFR !== undefined) {
            const actor = context.current?.actorIds[
              nodeNames.singleCenterLine
            ] as vtkActor;
            const isRCA = actor?.getReferenceByName?.('isRCA') === true;
            const shouldInclude =
              (name === 'rca' && isRCA) || (name === 'lca' && isRCA === false);

            const names = [
              name,
              ...(shouldInclude && config.ffr_selected_centerline_id !== null
                ? [nodeNames.singleCenterLine]
                : []),
              ...(name === 'aorta' ? ['FFR_aorta'] : []),
            ];

            if(name !== 'aorta'){
              handlehideFFRPoints(name === 'rca', visibleFFR);
            }
            setActorVisible(context, visibleFFR, names);
            updateRenderWindow(context);
            return;
          }
        }
        if (!newSegmentation) return;
        handleVisibility(
          context,
          analysisType,
          name,
          newSegmentation[name],
          setSvgShowText,
        );
        applyLesionStackMidNodeVisibility();
        applySurface3DOverlayVisibility();
      } else if (type === 'territory_result') {
        if (!context.current || !selectedSeriesId || !pool) return;
        const { result } = args;
        const keyData = pool['territory'];
        setBufferPool((prev) => ({
          ...prev,
          [selectedSeriesId]: {
            ...prev[selectedSeriesId],
            ['territory']: {
              ...keyData,
              loaded: false,
              loading: false,
            },
          },
        }));
        requestTerrData(
          pool,
          setBufferPool,
          accessToken,
          selectedSeriesId,
          result,
        );
      } else if (type === 'showOrHideSegmentation') {
        const { segmentation, isShow } = args;
        if (context.current) {
          console.log(isShow, segmentation);

          if (analysisType === 'FFR') {
            const FFR_TARGETS = [
              FFRNames.main_lca,
              FFRNames.main_rca,
              FFRNames.cap_lca,
              FFRNames.cap_rca,
              nodeNames.singleCenterLine,
            ];
            setActorVisible(context, isShow, FFR_TARGETS);
          }
          enterOrExitManualSegmentationVtk(
            context.current,
            isShow,
            segmentation,
          );
        }
      }else if(type === 'FFR_Manual_3D'){
        if(args.mode === 'Eraser'){
          svgDrawLineRef.current?.clear();
        }else if(args.mode === 'commit'){
          svgDrawLineRef.current?.commitFillPath();
        }else if(args.mode === 'dilate' || args.mode === 'erode'){
          svgDrawLineRef.current?.morphSelection(args.mode);
        }else if(args.mode === 'fill' || args.mode === 'outline'){
          setData({manualLabelmapStyle: args.mode})
          updateManualSegStyle(selectedSeriesId,args.mode);
        }else if(args.mode === 'coron'){
          const next = args.manualLabelmapStyle === 'outline' ? 'fill' : 'outline';
          setData({manualLabelmapStyle: next})
          updateManualSegStyle(selectedSeriesId,next);
        }else if(args.mode === 'InitialPointVisibility'){
          if(!context.current) return;
           setData({
             manual3DTool: {
               ...config.manual3DTool,
               initialPoint: args.visibility,
             },
           });

          setInitialPointSphereVisibility(
            context.current,
            args.visibility,
            context.current?.viewportId,
          );
        }
      }else if(args.type === 'lightingChange'){
        const { ambient, enabled } = args;
        setData((prev) => ({
          lightingOptions: {
            ...prev.lightingOptions,
            Surface3D: {
              enabled,
              ambient,
            },
          },
        }));
        updateAmbientLight(context, analysisType, { ambient, enabled });
        updateRenderWindow(context)
      }
    },
  };

  // 반드시 필요한 이닛
  useEffect(() => {
    if (!ref.current) return;

    let cntx = contextPool['Surface3D'] as SurfaceContext | undefined;

    if (shouldRecreateServerVtkContext(cntx)) {
      disposeServerFrontDataVtkContext(
        cntx,
        `Surface3D switch ${cntx?.activeAnalysisType ?? 'unknown'} -> ${analysisType}`,
      );
      cntx = undefined;
    }

    if (!cntx) {
      cntx = initializeVtkRenderer(
        'Surface3D',
        'Surface3D',
        config.colors,
      ) as SurfaceContext;
      setContextPool((prev) => ({ ...prev, Surface3D: cntx }));
    } else {
      const camera: vtkCamera = cntx.renderer.getActiveCamera();
      const focalPoint = camera.getFocalPoint();
      const distance = camera.getDistance();
      camera.setPosition(
        focalPoint[0],
        focalPoint[1] + distance,
        focalPoint[2],
      );
      camera.setViewUp(0, 0, -1);
      cntx.axes = {
        x: [1, 0, 0],
        y: [0, 1, 0],
        z: [0, 0, 1],
      };
    }

    if (!cntx) {
      throw new Error('Surface3D VTK context initialization failed');
    }

    cntx.serverFrontDataVtkMode = isServerFrontDataVtkMode;
    cntx.serverFrontDataVtkDisposed = false;
    cntx.activeAnalysisType = isServerFrontDataVtkMode ? analysisType : undefined;
    cntx.activeSeriesId = isServerFrontDataVtkMode ? selectedSeriesId : undefined;
    cntx.serverFrontDataRenderBlocked = isServerFrontDataVtkMode;
    if (isServerFrontDataVtkMode) {
      surfaceServerGenerationRef.current += 1;
    }
    setContainer(cntx, ref);
    if (config.EditMode && !config.viewportSwitchPendingMap.Surface3D) {
      restoreRendererActors(cntx);
      setData((prev) => ({
        viewportSwitchPendingMap: {
          ...prev.viewportSwitchPendingMap,
          Surface3D: true,
        },
      }));
    }

    if (config) {
      (context as React.MutableRefObject<SurfaceContext>).current = cntx;
    }

    return () => {
      if (cntx?.serverFrontDataVtkMode) {
        disposeServerFrontDataVtkContext(cntx, `Surface3D cleanup`);
        if (context.current === cntx) {
          context.current = null;
        }
        setContextPool((prev) =>
          prev.Surface3D === cntx ? { ...prev, Surface3D: null } : prev,
        );
        return;
      }

      cleanUpVtkRenderer(context, cntx);
      setContextPool((prev) => ({ ...prev, Surface3D: cntx }));
    };
  }, [
    ref,
    analysisType,
    selectedSeriesId,
    isServerFrontDataVtkMode,
    shouldRecreateServerVtkContext,
  ]);

  const isDataReady =
    Object.keys(requestSegmentation).every((key) => {
      const result = pool?.[`segmentationResult_${key}` as keyof typeof pool];
      return result?.loaded === true;
    }) &&
    (analysisType !== 'FFR' ||
      Object.values(FFRNames).every(
        (ffrKey) => pool?.[ffrKey as keyof typeof pool]?.loaded === true,
      ));

  // const isDataReady = Object.keys(requestSegmentation).every((key) => {
  //   const result = pool?.[`segmentationResult_${key}` as keyof typeof pool];
  //   return result?.loaded === true;
  // });
  useEffect(() => {
    if (
      !context.current ||
      !pool ||
      !analysisType ||
      !segmentation ||
      !isDataReady ||
      !selectedSeriesId
    )
      return;
    context.current.isAxis3DVisible = globalToolState?.toggles.ToggleAxes!;
    if (config.EditMode) return;
    const generation = surfaceServerGenerationRef.current;
    context.current.serverFrontDataVtkMode = isServerFrontDataVtkMode;
    context.current.activeAnalysisType = isServerFrontDataVtkMode
      ? analysisType
      : undefined;
    context.current.activeSeriesId = isServerFrontDataVtkMode
      ? selectedSeriesId
      : undefined;
    context.current.serverFrontDataRenderBlocked = isServerFrontDataVtkMode;
    if (isStaleServerSurfaceGeneration(generation)) return;
    Object.entries(requestSegmentation).forEach(
      async ([key, segmentationProps]) => {
        segmentation3DReader(
          context,
          selectedSeriesId,
          prevDataRef,
          pool,
          analysisType,
          key,
          segmentationProps,
        );
      },
    );

    if (isStaleServerSurfaceGeneration(generation)) return;
    updateResetCamera(context, analysisType, pool);
    setActorsCenter(context);

    if (getSegShapeAnalysisType(analysisType) && config.heartCut) {
      heartCutter(context, bufferPool!, config.heartCut, selectedSeriesId!);
    }

    if (analysisType === 'myocardial') {
      const polyData = pool[`segmentationResult_lvWall`].data as vtkPolyData;
      if (polyData.getPointData().getScalars()) {
        const lvWall = context.current.actorIds['lvWall'] as vtkActor;
        const coronary = context.current.actorIds['coronary'] as vtkActor;

        lvWall?.getMapper()?.setScalarVisibility(true);
        coronary?.getMapper()?.setScalarVisibility(false);
      }
    }
    if (analysisType === 'FFR') {
      segmentationFFRReader(context,selectedSeriesId ,analysisType, pool, segmentation);
    }
    setReadyState((prev) => ({ ...prev, panel: true, camera: false }));
  }, [analysisType, isDataReady]);

  // 시리즈 바뀌면 볼륨로드
  // useEffect(() => {
  //   if (!selectedSeriesId) return;
  //   if (isLayoutStateLoading) return;
  //   if (!segmentation) return;
  // //  if (analysisType === 'wallthickness') {
  // //     setStateLAWT(StateLAWT.NONE);
  // //     loadLAWT(selectedSeriesId, () => setStateLAWT(StateLAWT.READY), lowQuality);
  // //   }
  //   //if (
  //   //  analysisType !== 'segmentation' &&
  //   //  analysisType !== 'wallthickness' &&
  //   //  analysisType !== 'aorticValveCalcium'
  //   //) {
  //   //  loadBranches(selectedSeriesId);
  //   //}
  // }, [selectedSeriesId, isLayoutStateLoading, analysisType]);

  const { topContent, topLeftContentForCAC, midLeftContentRRPercent } =
    usePanelOverlayText({
      jsonRef,
    });
  const runtimeDisplayProfile = getDisplayRuntimeProfile();
  const ffrWarningMessage = useMemo(() => {
    if (analysisType !== 'FFR' || config.isCapture) return '';

    const coronary = (pool?.parameter?.data as { SavedInfo?: SavedInfo } | undefined)
      ?.SavedInfo?.Coronary;
    if (!coronary) return '';

    return (coronary.warningMessage_legacy ?? '').trim();
  }, [analysisType, config.isCapture, pool?.parameter?.data]);
  const ffrWarningFontSize = Math.round(14 * runtimeDisplayProfile.uiScale);
  const ffrWarningInsetTop = Math.round(28 * runtimeDisplayProfile.uiScale);
  const ffrWarningInsetRight = Math.round(8 * runtimeDisplayProfile.uiScale);
  const ffrWarningMaxWidth = Math.round(420 * runtimeDisplayProfile.uiScale);

  useResizeObserver({
    context,
    vtkContainerRef: ref,
    updateTextBoxPositions: () => {
      updateTextBoxPositions();
      updateMeasurementLabelPositions();
    },
  });

  // 이벤트 리쓴
  useEffect(() => {
    if (!ref.current) return;
    const container = ref.current;
    const mouseMoveHandler = (evt: MouseEvent) => {
      const activeToolId = panelToolState?.activeToolId
      handleMouseMove({
        context,
        evt,
        toolName: activeToolId === 'Eraser'? 'Rotation': activeToolId,
        analysisType,
      });
    };
    const mouseUpHandler = (evt: MouseEvent) =>
      handleMouseUp(context, evt, analysisType, selectedSeriesId);
    const mouseDownHandler = (evt: MouseEvent) =>
      handleMouseDown(context, evt, analysisType);
    const mouseWheelHandler = (evt: WheelEvent) =>
      handleMouseWheel(context, evt);

    container.addEventListener('mousedown', mouseDownHandler, {
      passive: false,
    });
    container.addEventListener('mouseup', mouseUpHandler, { passive: false });
    container.addEventListener('mousemove', mouseMoveHandler, {
      passive: false,
    });
    container.addEventListener('wheel', mouseWheelHandler, { passive: false });

    return () => {
      container.removeEventListener('mousemove', mouseMoveHandler);
      container.removeEventListener('mousedown', mouseDownHandler);
      container.removeEventListener('mouseup', mouseUpHandler);
      container.removeEventListener('wheel', mouseWheelHandler);
    };
  }, [context, panelToolState, analysisType, selectedSeriesId]);

  const [open, setOpen] = useState<Surface3DPanelPopup>(initialPopup);

  const handlePopupOpen = (key: keyof Surface3DPanelPopup) => {
    setOpen({ ...initialPopup, [key]: true });
  };
  const handlePopupClose = (key: keyof Surface3DPanelPopup) => {
    setOpen({ ...initialPopup, [key]: false });
  };

  const projectionContextMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    const only3DSwitchType = isVRSurfaceSwitchType(analysisType);

    const finalProjection = only3DSwitchType
      ? ['VR', 'Surface3D']
      : (possibleProjection ?? []);
    if (finalProjection.length === 0) {
      handlePopupClose('projection');
    }

    const isAvailableProjection = (componentKey: string) => {
      if (analysisType === 'FFR') {
       if (config.EditMode) return false;
      }
      return true;
    };

    const filteredProjection = finalProjection
      ?.filter((componentKey) => componentKey !== panelConfig.componentKey)
      ?.filter(isAvailableProjection);

    if (filteredProjection?.length === 0) {
      handlePopupClose('projection');
    }
    return (
      filteredProjection?.map((key) => ({
        label: key,
        onClick: () => {
          swapLayoutPanelConfig(
            panelConfig.componentKey,
            key as PanelComponentKeyType,
          );
          if (config.EditMode) {
            setData((prev) => ({
              viewportSwitchPendingMap: {
                ...prev.viewportSwitchPendingMap,
                [key]: false,
                Surface3D: false,
              },
            }));
          }
          if (analysisType === 'segmentation' && !only3DSwitchType)
            updateOtherViews([panelConfig.componentKey, key]);
          handlePopupClose('projection');
        },
        isChecked: panelConfig.componentKey === key,
      })) ?? []
    );
  }, [open['projection']]);

  const bottomLeftContent = useMemo(
    () => (
      <>
        {!!CArmAngles &&  !manualMode && (
          <div
            className="projection-text"
            onClick={() => handlePopupOpen('cArmAngles')}
          >
            {`${CArmAngles.primary} ${CArmAngles.secondary}`}
          </div>
        )}
        { (
          <div
            className={`projection-text ${panelConfig.componentKey}`}
            onClick={(e) => {
              //if (!getCenterLineDisplayType(analysisType) || analysisType //!=='segmentation') return;
              handlePopupOpen('projection');
            }}
          >
            {panelConfig.componentKey}
          </div>
        )}
      </>
    ),
    [CArmAngles],
  );

  const memoizedClassName = useMemo(
    () =>
      `panel ${className}${fullscreenPanelComponentKey === panelConfig.componentKey ? ' fullscreen' : ''}`,
    [
      fullscreenPanelComponentKey,
      className,
      // panelState.synchronizingTarget
    ],
  );

  // 더블 클릭시 전체 화면
  const hanldleFullScreenPanel = () => {
    if (isPreview) return;
    if (panelConfig.componentKey === fullscreenPanelComponentKey)
      updateFullscreenPanelComponentKey(undefined);
    else updateFullscreenPanelComponentKey(panelConfig.componentKey);
  };

  const readyCameraForAnalysis =
    analysisType === 'myocardial'
      ? pool?.centerData.loaded
      : analysisType === 'aorticValveCalcium'
        ? pool?.centerData.loaded &&
          (pool.centerData.data as Record<number, vtkPolyData>)[config.aorta_id]
        : analysisType === 'tavi'
          ? pool?.centerData.loaded &&
            !!(pool.centerData.data as Record<number, vtkPolyData>)[
              config.tavi_aorta_id ?? config.aorta_id
            ]
          : true && isDataReady;

  useEffect(() => {
    if (
      !readyCameraForAnalysis ||
      !context.current ||
      !selectedSeriesId ||
      !pool
    )
      return;
    if (!readyState.panel) return;
    const generation = surfaceServerGenerationRef.current;
    if (isStaleServerSurfaceGeneration(generation)) return;
    initCameraForAnalysis(context, analysisType, selectedSeriesId, pool);
    if (isStaleServerSurfaceGeneration(generation)) return;
    setReadyState((prev) => ({ ...prev, camera: true }));
  }, [readyCameraForAnalysis, readyState.panel]);

  const readyHighlight =
    analysisType !== 'myocardial' &&
    (analysisType === 'FFR'
      ? pool?.FFR_centerline.loaded
      : pool?.centerData.loaded) &&
    isDataReady;
  const editLumenInitialPointsSignature = useMemo(() => {
    const coronary = (
      pool?.parameter?.data as { SavedInfo?: SavedInfo } | undefined
    )?.SavedInfo?.Coronary;

    return [
      coronary?.initialPoints1?.join(',') ?? '',
      coronary?.initialPoints2?.join(',') ?? '',
    ].join('|');
  }, [pool?.parameter?.data]);

  // const readyHighlight =
  //   analysisType !== 'myocardial' && pool?.centerData.loaded && isDataReady;
  // //pool?.mapping_branches.loaded &&

  useEffect(() => {
    /**
     * fai mainBranch 3개
     * plaque 1개
     * node랑 lumen 한꺼번에 랜더링 처리
     * fai에서는 한번만 들어오겠지
     * plaque에서는 branchId 바꾸면 들어오겠지
     */
    if (!readyHighlight || !pool || !readyState.panel || config.EditMode) return;
    const generation = surfaceServerGenerationRef.current;
    if (isStaleServerSurfaceGeneration(generation)) return;
    const getBranches = (): number[] => {
      switch (analysisType) {
        case 'tavi':
          return [config.tavi_aorta_id];
        case 'fai':
        case 'calciumScore':
          return mainBranchIds.slice(0, 3);
        case 'plaque':
        case 'stenosis':
        case 'isr':
          return [plaueBranchMainId];
        case 'FFR':
          return config.ffr_selected_centerline_id != null
            ? [config.ffr_selected_centerline_id]
            : [];
        default:
          return [];
      }
    };
    const branch = getBranches();
    if (isStaleServerSurfaceGeneration(generation)) return;

    if (branch.length !== 0) {
      if (!['calciumScore', 'tavi', 'FFR'].includes(analysisType)) {
        CPRNodeAllRenderer(context, pool, branch, stateMap);
        applyLesionStackMidNodeVisibility();
      }
      if (analysisType === 'FFR') {
        updateFFRCentline(branch[0]);
      } else {
        centerLineRender(context, pool!, analysisType, branch);
      }
      applySurface3DOverlayVisibility();
    }else if (branch.length === 0 && analysisType !== 'FFR') {
      if (context.current) context.current.serverFrontDataRenderBlocked = false;
      return;
    }

    if (isStaleServerSurfaceGeneration(generation)) return;
    if (!context.current?.focus) {
      updateCameraForAnalysis(context, analysisType, selectedSeriesId!, pool);
    }

    if (analysisType === 'FFR') {
      setSvgShowText(true);
      return;
    }
    axesLineRenderer(context);

    const seg = panelStates.Surface3D?.segmentation;
    if (seg) {
      const key =
        analysisType === 'aorticValveCalcium' || analysisType === 'tavi'
          ? 'aorta'
          : 'coronary';
      handleVisibility(context, analysisType, key, seg[key], setSvgShowText);
      applyLesionStackMidNodeVisibility();
      applySurface3DOverlayVisibility();
    }
    if (context.current) {
      context.current.serverFrontDataRenderBlocked = false;
      scheduleRender(panelConfig.componentKey, { type: 'updateRenderWindow' });
    }
    //setReadyState((prev) => ({ ...prev, camera: false}));
  }, [
    readyState.camera,
    readyHighlight,
    analysisType === 'plaque' ? plaueBranchMainId : null,
    analysisType === 'isr' ? plaueBranchMainId : null,
    analysisType === 'stenosis' ? plaueBranchMainId : null,
    applyLesionStackMidNodeVisibility,
    applySurface3DOverlayVisibility,
  ]);

  useEffect(() => {
    const current = context.current;
    if (!current) return;

    const clearMarkers = () => {
      const changed = clearInitialPointSphereMarkers(current);
      if (changed) {
        scheduleRender(current.viewportId, { type: 'updateRenderWindow' });
      }
    };

    if (
      analysisType !== 'FFR' ||
      config.EditMode !== EditNames.lumen ||
      !selectedSeriesId ||
      !pool
    ) {
      clearMarkers();
      return;
    }

    const half = config.generalSetting.renderingQuality !== 'high';
    const volume = getVolumeFromSeriesId(selectedSeriesId, half);
    if (!volume) {
      clearMarkers();
      return;
    }

    const spacing = volume.spacing as [number, number, number];
    const origin = volume.origin as [number, number, number];
    const dimensions = volume.dimensions as [number, number, number];
    const markerRadius = Math.max(2, Math.min(6, Math.max(...spacing) * 3));
    const specs = buildInitialPointSphereMarkerSpecs({
      parameter: pool.parameter?.data as { SavedInfo?: SavedInfo } | undefined,
      spacing,
      origin,
      dimensions,
      half,
      indexToWorld: getIndexToWorld,
    });
    const changed = syncInitialPointSphereMarkers(current, specs, {
      radius: markerRadius,
    });

    if (changed) {
      scheduleRender(current.viewportId, { type: 'updateRenderWindow' });
    }

    return clearMarkers;
  }, [
    analysisType,
    config.EditMode,
    config.generalSetting.renderingQuality,
    editLumenInitialPointsSignature,
    pool,
    selectedSeriesId,
  ]);

  useEffect(() => {
    /**
     * fai에서만 리셋버튼누르면 동작하는부분
     */
    if (analysisType !== 'fai' || !resetNodeRef.current) return;
    if (!stateMap || !pool) return;
    CPRNodeAllRenderer(context, pool, mainBranchIds.slice(0, 3), stateMap);
    resetNodeRef.current = false;
  }, [resetNodeRef, stateMap]);

  const readyTaviData =
    analysisType === 'tavi' &&
    !!pool?.['tavi.annulus']?.loaded &&
    !!pool?.['tavi.sinotubularjunction']?.loaded &&
    !!pool?.['tavi.sinus']?.loaded &&
    !!pool?.['tavi.parsed']?.data &&
    !!pool?.centerData.loaded &&
    !!pool?.segmentationVolumeMapper.loaded;
  //&& pool?.mapping_branches.loaded

  useEffect(() => {
    if (!selectedSeriesId) return;
    /**
     * tavi 전용~
     */
    if (!readyTaviData) return;
    console.log('tavi node 왔능가', pool?.['tavi.parsed']);

    taviNodeAllRenderer(context, pool, selectedSeriesId);
    //CPRNodeAllRenderer(context, pool, mainBranchIds.slice(0, 3), stateMap);
  }, [readyTaviData]);

  const readyWallthickness =
    analysisType === 'wallthickness' &&
    (stateLAWT !== StateLAWT.NONE || pool?.LAWT?.data) &&
    pool?.segmentationResult_laWall?.data;
  // && pool?.LAWT.loaded;

  const readyLocalVolume =
    analysisType === 'wallthickness' &&
    pool?.LAWT?.loaded &&
    pool?.segmentationResult_laWall?.loaded;

  useEffect(() => {
    if (!pool || !selectedSeriesId || !readyLocalVolume) return;
    if (!pool?.LAWT?.data && pool?.LAWT?.loaded) {
      setStateLAWT(StateLAWT.NONE);
      loadLAWT(
        selectedSeriesId,
        () => setStateLAWT(StateLAWT.READY),
        lowQuality,
      );
      console.log('그냥 mapping으로');
    }
  }, [readyLocalVolume]);

  useEffect(() => {
    if (!context.current || !readyWallthickness) return;
    if (!pool || !selectedSeriesId) return;
    //wallthicknessRenderer(context, pool!, selectedSeriesId!);
    mappingRenderer(context, pool!, selectedSeriesId!, analysisType, 'laWall');
    setStateLAWT(StateLAWT.COMPLETE);
    console.log('wallthickness 왔능가');
  }, [readyWallthickness]);

  const territoryData = pool?.territory?.data;
  const readyterritory =
    analysisType === 'myocardial' && territoryData && isDataReady;

  useEffect(() => {
    if (
      !context.current ||
      !readyterritory ||
      !pool?.territory.loaded ||
      !selectedSeriesId
    )
      return;
    if (!pool?.territory.data) return;
    console.time('territory Mapping');
    console.log('territory 왔능가');
    jsonRef.current = (territoryData as territoryInfo)?.json;
    applyMyoCardialConfig(config, jsonRef.current);
    createTerritoryCenterLine(context, pool, selectedSeriesId!, analysisType);
    mappingRenderer(context, pool!, selectedSeriesId!, analysisType, 'lvWall');
    //lumenRenderer(context, selectedSeriesId!, analysisType, pool, []);
    closeLoadingModal();
    console.timeEnd('territory Mapping');
  }, [readyterritory, territoryData]);

  // useKeyboardShortcut('y',(e:KeyboardEvent)=>{
  //   // const container = document.getElementById('renderererer');
  //   if(!ref.current)return;
  //   context.current?.renderer.setBackground(0,0,0,0);
  //   ref.current.style.backgroundImage=
  //     // ...ref.current.style,
  //     `url(${test})`
  //   ref.current.style.backgroundSize='100%';
  //   ref.current.style.backgroundRepeat='no-repeat';
  //   ref.current.style.backgroundPosition='center';

  // })

  return (
    <div
      className={memoizedClassName}
      onMouseDown={() => {
        if (selectedPanelComponentKey !== panelConfig.componentKey) {
          deactivateExclusiveTools({ viewportId: selectedPanelComponentKey });
        }
        updateSelectedPanelConfig(panelConfig);
      }}
      onDoubleClick={() => {
        if (Object.values(open).some(Boolean) || analysisType === 'chamber')
          return;
        hanldleFullScreenPanel();
      }}
       onMouseUp={(event) => {
        svgDrawLineRef.current?.cancelInteraction();
      }}
    >
      <div
        id="renderererer"
        className={`renderer ${panelConfig.componentKey}`}
        ref={ref}
      />
      {manualMode &&
        selectedPanelComponentKey === 'Surface3D' &&
        context.current && (
          <SvgDrawLine
            ref={svgDrawLineRef}
            containerRef={ref}
            renderer={context.current?.renderer}
            context={context.current}
            lowQuality={lowQuality}
            activeToolId={panelToolState?.activeToolId}
          />
        )}
      {!!ffrWarningMessage && (
        <div
          style={{
            position: 'absolute',
            top: ffrWarningInsetTop,
            right: ffrWarningInsetRight,
            maxWidth: `min(40%, ${ffrWarningMaxWidth}px)`,
            color: '#FFFFFF',
            fontSize: ffrWarningFontSize,
            lineHeight: 1.4,
            textAlign: 'right',
            whiteSpace: 'pre-wrap',
            textShadow: 'black 0px 0px 3px',
            pointerEvents: 'none',
            zIndex: 4,
          }}
        >
          {ffrWarningMessage}
        </div>
      )}
      {canRenderSvgText &&
        svgShowText &&
        surface3DOverlayLabel.label !== false &&
        !config.EditMode &&
        (analysisType === 'FFR' || !config.isCapture) && (
          <SvgTextRenderer
            svgRef={svgRef}
            containerRef={ref}
            renderer={context.current!.renderer}
            dataSource={filteredCenterlineItems}
            onCameraRotate={updateTextBoxPositions}
            onDelete={handleDelete}
          />
        )}
      {['wallthickness', 'FFR'].includes(analysisType) &&
        ['Surface3D', 'Empty', undefined].includes(
          fullscreenPanelComponentKey,
        ) &&
        svgRef &&
        ref.current &&
        (analysisType === 'FFR' || !config.isCapture) &&
        !config.EditMode && (
          <SvgColorScaleBar
            variant="panel"
            svgRef={svgRef}
            containerRef={ref}
            min={analysisType === 'FFR' ? 0.5 : 0.0}
            max={analysisType === 'FFR' ? 1.0 : 5.5}
            displayValue={wallThicknessValue}
          />
        )}
      {measurementLabelItems.length > 0 && (
        <SvgMeasurementText
          svgRef={measurementSvgRef}
          containerRef={ref}
          labels={measurementLabelItems}
        />
      )}
      {globalToolState?.toggles.ToggleAnnotations && (
        <>
          {' '}
          <PanelOverlay
            config={{
              left: {
                content:
                  !manualMode && analysisType === 'segmentation'
                    ? topContent
                    : '',
              },
              'top-left': {
                content:
                  analysisType === 'calciumScore' ||
                  analysisType === 'aorticValveCalcium' ||
                  analysisType === 'myocardial' ||
                  (!manualMode && analysisType === 'segmentation')
                    ? topLeftContentForCAC
                    : '',
              },
              'top-right': {
                content:
                  analysisType === 'FFR' ||
                  analysisType === 'calciumScore' ||
                  analysisType === 'aorticValveCalcium' ||
                  analysisType === 'myocardial'
                    ? ''
                    : topRightContent,
              },
              'bottom-left': {
                content:
                  analysisType === 'FFR' ? (
                    config.EditMode ? (
                      <>
                        {bottomLeftContent}
                        {surface3DOverlayButtonContent}
                      </>
                    ) : null
                  ) : config.EditMode ? (
                    <>
                      {bottomLeftContent}
                      {surface3DOverlayButtonContent}
                    </>
                  ) : (
                    <>
                      {midLeftContentRRPercent}
                      {bottomLeftContent}
                      {surface3DOverlayButtonContent}
                    </>
                  ),
              },
              // 'bottom-right': {
              //   content: bottomRightContent
              // }
            }}
            originComponentKey={panelConfig.componentKey}
          />
        </>
      )}
      {open['projection'] && (
        <SimpleContextMenu
          items={projectionContextMenuItems}
          onClose={() => handlePopupClose('projection')}
        />
      )}
      {surface3DOverlayMenuOpen && shouldShowSurface3DOverlayMenu && (
        <SimpleContextMenu
          items={surface3DOverlayMenuItems}
          onClose={() => setSurface3DOverlayMenuOpen(false)}
          checkbox={true}
        />
      )}
      {open['cArmAngles'] &&
        handleOpenCArmAnglesModal(() => handlePopupClose('cArmAngles'))}
    </div>
  );
};

export default Surface3DPanel;
